import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "error"
}

import { computed, inject, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import { ColorScaleInterpolate } from '@/utils/colorScale'
import { FormatOptions, formatter } from '../formatter'
import { useDataSource } from '@/composables/useDataSources'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { useI18n } from 'vue-i18n'
import { EntityLevel, useEntityLevel } from '@/composables/useEntityLevel'
import HeatmapPagination from '@/design-system/atoms/HeatmapPagination.vue'
import { sharedDataKey } from '../../types'
import TextBase from '@/design-system/atoms/TextBase.vue'
import { storeToRefs } from 'pinia'
import { Measurement } from '@/features/dashboard'
import { MeasurementPoint } from '../measurements/contracts'
import { useComponentFilter } from '../measurements/useMeasurements'
import HeatmapTableNew from '@/design-system/molecules/HeatmapTableNew.vue'
import CollapseControllerSummaryNew from './CollapseController/CollapseControllerSummaryNew.vue'

type Props = {
  title: string
  featureName: string
  info?: string
  measurement: Measurement
  colorScale: ColorScaleInterpolate
  enabledEntityLevels?: EntityLevel[]
  errorMessages?: Record<ErrorState, LocaleString>
  subMeasurementToTitle: Record<string, LocaleString>
  noReverse?: boolean
}

type ErrorState = 'NO_DATA' | 'INVALID_FILTER'

type ProcessedHeatmapData = {
  axes: {
    x: (string | null)[] // HeatmapTable expects string | null
    y: (string | null)[] // HeatmapTable expects string | null
  }
  summary: {
    x: (number | null)[] // Adjust type if summary is calculated
    y: (number | null)[] // Adjust type if summary is calculated
  }
  matrix: (number | null)[][]
  tooltipLabelsMatrix: string[][]
}


export default /*@__PURE__*/_defineComponent({
  __name: 'CardHeatmapNew',
  props: {
    title: {},
    featureName: {},
    info: {},
    measurement: {},
    colorScale: {},
    enabledEntityLevels: {},
    errorMessages: {},
    subMeasurementToTitle: {},
    noReverse: { type: Boolean }
  },
  setup(__props: any) {

const props = __props
const { colorScale, measurement, subMeasurementToTitle } = toRefs(props)

const { locale, t } = useI18n()

useComponentFilter(props.featureName, {})
const dataSourceId = computed(
  () =>
    `measurements.${props.featureName}.${measurement.value.name}.${measurement.value.field.name}.heatmap.values`
)
const dataSource = useDataSource<MeasurementPoint[]>(dataSourceId)

const dataFormat = computed(() => measurement.value.options.format)

const { entityLevel } = storeToRefs(useEntityLevel())

const heatmapData = computed<{ isLoading: boolean; data: ProcessedHeatmapData | null }>(() => {
  const isLoading = dataSource.value?.isLoading ?? true
  const firstDataSourceWithData = dataSource.value

  if (isLoading) {
    return { isLoading: true, data: null }
  }

  const emptyDataStructure: ProcessedHeatmapData = {
    axes: { x: [], y: [] },
    summary: { x: [], y: [] },
    matrix: [],
    tooltipLabelsMatrix: []
  }

  if (!firstDataSourceWithData?.data || firstDataSourceWithData.data.length === 0) {
    return { isLoading: false, data: emptyDataStructure }
  }

  const points = firstDataSourceWithData.data
  const firstPointGroups = points[0]?.groups

  if (!firstPointGroups || Object.keys(firstPointGroups).length < 2) {
    console.warn('Heatmap data points lack sufficient group keys.') // Keep warnings for now
    return { isLoading: false, data: emptyDataStructure }
  }

  let yAxisKey: string | undefined = undefined
  let xAxisKey: string | undefined = undefined
  const groupKeys = Object.keys(firstPointGroups)

  if (groupKeys.includes('timestamp_extract_hour')) {
    yAxisKey = 'timestamp_extract_hour'
  } else {
    yAxisKey = groupKeys[0] // Fallback to first key
  }

  if (groupKeys.includes('timestamp_extract_isodow')) {
    xAxisKey = 'timestamp_extract_isodow'
  } else {
    // Fallback to the next key that isn't the yAxisKey
    xAxisKey = groupKeys.find((key) => key !== yAxisKey) ?? groupKeys[1]
  }

  if (!yAxisKey || !xAxisKey || yAxisKey === xAxisKey) {
    console.warn('Could not determine distinct X and Y axis keys for heatmap.')
    return { isLoading: false, data: emptyDataStructure }
  }

  const yValues = new Set<string | number>()
  const xValues = new Set<string | number>()
  const valueMap = new Map<string, number>()

  points.forEach((point) => {
    const groups = point.groups
    const fields = point.fields
    if (!groups || !fields) return

    const yVal = groups[yAxisKey!]
    const xVal = groups[xAxisKey!]
    const fieldKeys = Object.keys(fields)

    if (
      yVal !== undefined &&
      yVal !== null &&
      xVal !== undefined &&
      xVal !== null &&
      fieldKeys.length > 0
    ) {
      if (typeof yVal === 'string' || typeof yVal === 'number') {
        yValues.add(yVal)
      }
      if (typeof xVal === 'string' || typeof xVal === 'number') {
        xValues.add(xVal)
      }

      const value = fields[fieldKeys[0]]
      if (typeof value === 'number') {
        valueMap.set(`${yVal}-${xVal}`, value)
      }
    }
  })

  const sortedY: (string | number)[] = Array.from(yValues).sort((a, b) =>
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))
  )

  const sortedX = Array.from(xValues).sort((a, b) =>
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b))
  )

  if (sortedX.length === 0) {
    console.warn('No valid data points found to create heatmap X axis.')
    return { isLoading: false, data: emptyDataStructure }
  }

  const yIndexMap = new Map(sortedY.map((val, i) => [val, i]))
  const xIndexMap = new Map(sortedX.map((val, i) => [val, i]))

  const matrix: (number | null)[][] = Array.from({ length: sortedY.length }, () =>
    Array(sortedX.length).fill(null)
  )

  valueMap.forEach((value, key) => {
    const separatorIndex = key.lastIndexOf('-')
    if (separatorIndex === -1) return // Invalid key format

    const yValStr = key.substring(0, separatorIndex)
    const xValStr = key.substring(separatorIndex + 1)

    const yVal = sortedY.find((v) => String(v) === yValStr)
    const xVal = sortedX.find((v) => String(v) === xValStr)

    if (yVal !== undefined && xVal !== undefined) {
      const rowIndex = yIndexMap.get(yVal)
      const colIndex = xIndexMap.get(xVal)
      if (rowIndex !== undefined && colIndex !== undefined) {
        matrix[rowIndex][colIndex] = value
      } else {
        console.warn(`Could not find index for yVal: ${yVal} or xVal: ${xVal}`)
      }
    } else {
      console.warn(`Could not find original value for yValStr: ${yValStr} or xValStr: ${xValStr}`)
    }
  })

  const nonEmptyRowIndices = sortedY
    .map((_, i) => i)
    .filter((rowIndex) => matrix[rowIndex].some((cell) => cell !== null))

  const nonEmptyColIndices = sortedX
    .map((_, j) => j)
    .filter((colIndex) => matrix.some((row) => row[colIndex] !== null))

  const filteredY = nonEmptyRowIndices.map((i) => sortedY[i])
  const filteredX = nonEmptyColIndices.map((j) => sortedX[j])

  const filteredMatrix = nonEmptyRowIndices.map((rowIndex) =>
    nonEmptyColIndices.map((colIndex) => matrix[rowIndex][colIndex])
  )

  if (filteredX.length === 0 || filteredY.length === 0) {
    console.warn('All rows/columns were empty after filtering.')
    return { isLoading: false, data: emptyDataStructure }
  }

  const summaryY = filteredMatrix.map((row) =>
    row.reduce((sum: number, cell) => sum + (cell ?? 0), 0)
  )

  const summaryX = filteredX.map(
    (_, colIndex) => filteredMatrix.reduce((sum: number, row) => sum + (row[colIndex] ?? 0), 0) // Explicitly type accumulator 'sum' as number
  )

  const tooltipLabelsMatrix = filteredY.map((yLabel) => {
    return filteredX.map((xLabel) => {
      let tooltipXFormat = computedXAxisFormat.value // Default to the axis format
      if (computedXAxisFormat.value?.format?.type === 'isodow') {
        tooltipXFormat = { format: { type: 'isodow', weekday: 'long' } }
      }
      // Explicitly check for null/undefined instead of truthiness to handle 0 correctly
      const formattedXLabel =
        xLabel !== null && xLabel !== undefined
          ? formatter(xLabel, locale.value, tooltipXFormat)
          : '?'
      const formattedYLabel =
        yLabel !== null && yLabel !== undefined
          ? formatter(yLabel, locale.value, computedYAxisFormat.value)
          : '?'
      return `${formattedXLabel} - ${formattedYLabel}`
    })
  })

  const processedData: ProcessedHeatmapData = {
    axes: { x: filteredX.map(String), y: filteredY.map(String) },
    summary: { x: summaryX, y: summaryY },
    matrix: filteredMatrix,
    tooltipLabelsMatrix
  }

  return { isLoading: false, data: processedData }
})

const axisKeys = computed<{ xAxisKey?: string; yAxisKey?: string }>(() => {
  const points = dataSource.value?.data
  if (!points || points.length === 0) return {}

  const firstPointGroups = points[0]?.groups
  if (!firstPointGroups || Object.keys(firstPointGroups).length < 2) return {}

  let yAxisKey: string | undefined = undefined
  let xAxisKey: string | undefined = undefined
  const groupKeys = Object.keys(firstPointGroups)

  if (groupKeys.includes('timestamp_extract_hour')) {
    yAxisKey = 'timestamp_extract_hour'
  } else {
    yAxisKey = groupKeys[0]
  }

  if (groupKeys.includes('timestamp_extract_isodow')) {
    xAxisKey = 'timestamp_extract_isodow'
  } else {
    xAxisKey = groupKeys.find((key) => key !== yAxisKey) ?? groupKeys[1]
  }

  if (!yAxisKey || !xAxisKey || yAxisKey === xAxisKey) {
    return {} // Invalid keys
  }

  return { xAxisKey, yAxisKey }
})

const xAxisKey = computed(() => axisKeys.value.xAxisKey)
const yAxisKey = computed(() => axisKeys.value.yAxisKey)

const computedXAxisFormat = computed<FormatOptions>((): FormatOptions => {
  if (xAxisKey.value === 'timestamp_extract_isodow') {
    return { format: { type: 'isodow', weekday: 'short' } }
  }
  // Add other potential key checks here
  return { format: { type: 'intl-number' } }
})

const computedXSumLabel = computed<string>(() => {
  if (xAxisKey.value === 'timestamp_extract_isodow') {
    return {
      sv: 'Sum dag',
      en: 'Sum day'
    }[locale.value]!
  }
  return {
    sv: 'Sum',
    en: 'Sum'
  }[locale.value]!
})

const computedYSumLabel = computed<string>(() => {
  if (yAxisKey.value === 'timestamp_extract_hour') {
    return {
      sv: 'Sum tid',
      en: 'Sum time'
    }[locale.value]!
  }
  return {
    sv: 'Sum',
    en: 'Sum'
  }[locale.value]!
})

const computedYAxisFormat = computed<FormatOptions>((): FormatOptions => {
  if (yAxisKey.value === 'timestamp_extract_hour') {
    return { format: { type: 'hour-range', hour: '2-digit', minute: '2-digit' } }
  }
  // Add other potential key checks here
  return { format: { type: 'intl-number' } }
})

const currentPage = ref(0)
const fullscreenHeatmap = ref(false)

const sharedData = inject(sharedDataKey)

const fullscreen = computed(() => sharedData?.fullscreen ?? false)

const correctFilter = computed(
  () =>
    (props.enabledEntityLevels && props.enabledEntityLevels.includes(entityLevel.value)) ||
    !props.enabledEntityLevels
)

const paginationTitle = computed(() => {
  const xLabels = heatmapData.value?.data?.axes.x
  if (
    !xLabels ||
    xLabels.length === 0 ||
    itemsPerPage.value <= 0 ||
    start.value >= xLabels.length
  ) {
    return ''
  }

  const firstLabel = xLabels[start.value]
  // Calculate the index of the last visible item, ensuring it's within bounds
  const lastLabelIndex = Math.min(end.value - 1, xLabels.length - 1)
  const lastLabel = xLabels[lastLabelIndex]

  // Format the labels using the computed X-axis format
  const formattedFirstLabel = firstLabel
    ? formatter(firstLabel, locale.value, computedXAxisFormat.value)
    : '-'
  const formattedLastLabel = lastLabel
    ? formatter(lastLabel, locale.value, computedXAxisFormat.value)
    : '-'

  // Handle case where only one item is shown or start/end are the same
  if (start.value === lastLabelIndex || formattedFirstLabel === formattedLastLabel) {
    return formattedFirstLabel
  }

  return `${formattedFirstLabel} - ${formattedLastLabel}`
})

const itemsPerPage = computed(() => {
  if (fullscreenHeatmap.value || fullscreen.value) {
    return 21
  }
  return 7
})

const daysCount = computed(() => heatmapData.value?.data?.axes.x.length ?? 0)

const maxPage = computed(() => Math.max(0, Math.ceil(daysCount.value / itemsPerPage.value) - 1))

const start = computed(() => {
  if (itemsPerPage.value <= 0) return 0
  return currentPage.value * itemsPerPage.value
})

const end = computed(() => {
  if (itemsPerPage.value <= 0) return daysCount.value
  return Math.min(start.value + itemsPerPage.value, daysCount.value)
})

watch(
  [maxPage, itemsPerPage],
  () => {
    // Ensure currentPage doesn't exceed the new maxPage
    if (currentPage.value > maxPage.value) {
      currentPage.value = maxPage.value
    }
    if (!props.noReverse && currentPage.value !== maxPage.value) {
      currentPage.value = maxPage.value
    }
  },
  { immediate: true }
)

function stepLeft() {
  if (currentPage.value > 0) {
    currentPage.value--
  }
}

function stepRight() {
  if (currentPage.value < maxPage.value) {
    currentPage.value++
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(CardInteractive, {
    title: _ctx.title,
    info: _ctx.info,
    enableFullscreen: true,
    isLoading: heatmapData.value && heatmapData.value.isLoading && correctFilter.value,
    showControls: true,
    headerDivider: true,
    onToggleFullscreen: _cache[0] || (_cache[0] = ($event: any) => (fullscreenHeatmap.value = $event))
  }, {
    controls: _withCtx(() => [
      _createVNode(HeatmapPagination, {
        totalItems: heatmapData.value?.data?.axes.x.length ?? 0,
        itemsPerPage: itemsPerPage.value,
        currentPage: currentPage.value,
        pageTitle: paginationTitle.value,
        onStepLeft: stepLeft,
        onStepRight: stepRight
      }, null, 8, ["totalItems", "itemsPerPage", "currentPage", "pageTitle"])
    ]),
    footerControls: _withCtx(() => [
      _createTextVNode(_toDisplayString(_unref(t)('dashboard__text__selection')) + " : ", 1),
      _createVNode(CollapseControllerSummaryNew, {
        showTimeSelection: true,
        fullscreen: fullscreen.value
      }, null, 8, ["fullscreen"])
    ]),
    default: _withCtx(() => [
      (heatmapData.value?.data?.axes.x.length && correctFilter.value)
        ? (_openBlock(), _createBlock(HeatmapTableNew, {
            key: 0,
            fullscreen: fullscreenHeatmap.value,
            matrix: heatmapData.value?.data,
            "color-scale": _unref(colorScale),
            subMeasurementToTitle: _unref(subMeasurementToTitle),
            xSumLabel: computedXSumLabel.value,
            ySumLabel: computedYSumLabel.value,
            dataFormat: dataFormat.value,
            xAxisFormat: computedXAxisFormat.value,
            yAxisFormat: computedYAxisFormat.value,
            start: start.value,
            end: end.value
          }, null, 8, ["fullscreen", "matrix", "color-scale", "subMeasurementToTitle", "xSumLabel", "ySumLabel", "dataFormat", "xAxisFormat", "yAxisFormat", "start", "end"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_1, [
            (correctFilter.value)
              ? (_openBlock(), _createBlock(TextBase, {
                  key: 0,
                  text: _ctx.errorMessages?.NO_DATA[_unref(locale)],
                  type: "label"
                }, null, 8, ["text"]))
              : (_openBlock(), _createBlock(TextBase, {
                  key: 1,
                  text: _ctx.errorMessages?.INVALID_FILTER[_unref(locale)],
                  type: "label"
                }, null, 8, ["text"]))
          ]))
    ]),
    _: 1
  }, 8, ["title", "info", "isLoading"]))
}
}

})